import { useCallback, useEffect, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import { useCheckoutStore } from './useCheckoutStore';

type UseCheckoutLauncherOpts = {
  // Drawer's own onComplete only closes it (see MainLayout), so page refresh
  // belongs here — fired when the drawer closes. `completed` says whether the
  // session paid (PaymentStep marks it) rather than being dismissed.
  onClosed?: (info: { completed: boolean }) => void;
};

// Shared launcher for the single checkout-v2 drawer (mounted in MainLayout).
// Opens via the store so every entry point behaves identically.
export function useCheckoutLauncher({ onClosed }: UseCheckoutLauncherOpts = {}) {
  const { user } = useAuth();
  const drawerOpen = useCheckoutStore((s) => s.drawerOpen);

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justClosed = wasOpenRef.current && !drawerOpen;
    wasOpenRef.current = drawerOpen;
    if (justClosed && user?.tcId && onClosedRef.current) {
      const completed = Boolean(useCheckoutStore.getState().completedAt);
      // Delay so billing-manager reflects the new subscription first.
      const timer = setTimeout(() => onClosedRef.current?.({ completed }), 600);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, user?.tcId]);

  const openCheckout = useCallback(() => useCheckoutStore.getState().openDrawer(), []);

  return { openCheckout };
}
